const state = () => ({
    loading: true,
    moreLoading: false,
    breadcrumbs: [],
    data: [],
    currentId: null,
})

// getters
const getters = {}

// actions
const actions = {
    // get directory and video
    async get({state, commit, rootState, dispatch}, {id, filter, sort, pagination, fetchTreeStatus} = {}) {
        commit('setLoading', true);
        commit('setCurrentId', id ?? 0);

        if (!rootState.library.directory.tree.data.length || fetchTreeStatus) {
            await dispatch('library/directory/tree/get', {}, {root: true});
        }
        const directoryTree = rootState.library.directory.tree.data;
        const idMap = buildIdMap(directoryTree);
        const directory = idMap[id] ?? {children: directoryTree};
        const directoryChildren = directory?.children || [];

        const filteredDirectories = applyFilterAndSort(
            directoryChildren,
            filter,
            sort
        );

        let pageSize = pagination?.page ?? 1;
        let pageLimit = pagination?.page_limit ?? 20;
        let paginationFilter = {
            page: pageSize,
            // klasör sayısı limitten fazlaysa fark negatife düşüyor ve API 400 dönüyordu
            page_limit: (pageSize === 1)
                ? Math.max(pageLimit - (filteredDirectories?.length ?? 0), 1)
                : pageLimit,
        }

        try {
            await dispatch('library/video/get', {directory_id: id != null && id !== 0 ? id : null, filter: {...filter}, pagination: paginationFilter, sort: sort}, {root: true});
        } catch (error) {
            // istek başarısız olursa loading açık kalmasın (sonsuz skeleton)
            commit('setLoading', false);
            throw error;
        }

        // istek uçuştayken başka klasöre geçildiyse bu cevabı uygulama
        if (String(state.currentId ?? 0) !== String(id ?? 0)) return;

        const videos = rootState.library.video.data;

        const data = [
            ...filteredDirectories.map((item) => ({
                ...item,
                type: 'directory'
            })),
            ...(videos || []).map((item) => ({
                ...item,
                id: 'video-' + item.id,
                video_id: item.id,
                type: 'video'
            }))
        ];

        const breadcrumbs = id != null && id !== 0 ? getBreadcrumbs(id, idMap) : [];
        commit('setData', data);
        commit('setBreadCrumbs', breadcrumbs);
        commit('setLoading', false);
    },

    async getMore({state, commit, rootState, dispatch}, {id, filter, sort, pagination, fetchTreeStatus} = {}) {
        commit('setMoreLoading', true);

        let applied;
        try {
            applied = await dispatch('library/video/get', {directory_id: id != null && id !== 0 ? id : null, filter: {...filter}, pagination: pagination, sort: sort}, {root: true});
        } catch (error) {
            commit('setMoreLoading', false);
            throw error;
        }

        // istek uçuştayken başka klasöre geçildiyse eski klasörün videolarını ekleme
        if (applied === false || String(state.currentId ?? 0) !== String(id ?? 0)) {
            commit('setMoreLoading', false);
            return;
        }

        const videos = rootState.library.video.data;
        const data = [
            ...(videos || []).map((item) => ({
                ...item,
                id: 'video-' + item.id,
                video_id: item.id,
                type: 'video'
            }))
        ];

        commit('appendData', data);
        commit('setMoreLoading', false);
    },

    // move directory and video
    async move({state, commit, rootState, dispatch}, {data} = {}) {
        const results = [];

        for (const item of data.items) {

            if (item.type === 'directory') {
                const updateRes = await dispatch(
                    'library/directory/update',
                    {data: {id: item.id, title: item.title, parent_id: data.parent_id}},
                    {root: true}
                );

                results.push(updateRes.data);
            }

            if (item.type === 'video') {
                const updateRes = await dispatch(
                    'library/video/moveVideo',
                    {data: {id: item.video_id, directory_id: data.parent_id}},
                    {root: true}
                );

                results.push(updateRes.data);
            }

        }

        return {status: true};
    },

    // move directory and video
    async delete({state, commit, rootState, dispatch}, {data} = {}) {
        const results = [];

        for (const item of data.items) {

            if (item.type === 'directory') {
                const updateRes = await dispatch(
                    'library/directory/delete',
                    {data: {id: item.id}},
                    {root: true}
                );

                results.push(updateRes.data);
            }

            if (item.type === 'video') {
                const updateRes = await dispatch(
                    'library/video/delete',
                    {data: {id: item.video_id}},
                    {root: true}
                );

                results.push(updateRes.data);
            }

        }

        return {status: true};

    }
}

// mutations
const mutations = {
    setLoading(state, loading) {
        state.loading = loading
    },
    setMoreLoading(state, loading) {
        state.moreLoading = loading
    },
    setBreadCrumbs(state, breadcrumbs) {
        state.breadcrumbs = breadcrumbs
    },
    setData(state, data) {
        state.data = data
    },
    appendData(state, data) {
        state.data = [...state.data, ...data]
    },
    setCurrentId(state, id) {
        state.currentId = id
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
}

function applyFilterAndSort(items, filter, sort) {
    let result = [...items];

    // FILTER
    if (filter?.title) {
        result = result.filter(item =>
            item.title?.toLowerCase().includes(filter.title.toLowerCase())
        );
    }

    if (filter?.id) {
        result = result.filter(item => item.id == filter.id);
    }

    // SORT
    if (sort) {
        const [by, direction] = sort.split(':');

        result.sort((a, b) => {
            let valA = a[by];
            let valB = b[by];

            if (typeof valA === 'string') valA = valA.toLowerCase();
            if (typeof valB === 'string') valB = valB.toLowerCase();

            if (valA < valB) return direction === 'asc' ? -1 : 1;
            if (valA > valB) return direction === 'asc' ? 1 : -1;
            return 0;
        });
    }

    return result;
}

function findNodeById(tree, id) {
    for (const node of tree) {
        if (node.id === id) return node
        if (node.children?.length) {
            const found = findNodeById(node.children, id)
            if (found) return found
        }
    }
    return null
}

function buildIdMap(tree, map = {}) {
    for (const node of tree) {
        map[node.id] = node;
        if (node.children?.length) {
            buildIdMap(node.children, map);
        }
    }
    return map;
}

function getBreadcrumbs(nodeId, idMap) {
    const breadcrumbs = [];
    let current = idMap[nodeId];

    while (current) {
        breadcrumbs.unshift({
            id: current.id,
            title: current.title
        });
        current = current.parent_id ? idMap[current.parent_id] : null;
    }

    return breadcrumbs;
}
